import { useState, useMemo } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK } from "@/lib/app";
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from "recharts";
import PinButton from "@/components/PinButton";
import PeriodOverrideBar from "@/components/PeriodOverrideBar";

const fetcher = (url) => api.get(url).then((r) => r.data);

const COLORS_LINES = ["#2B4C38", "#C78220", "#3E7B52", "#BA3F2B", "#7FAB8C", "#5E6962", "#E09B6E", "#1E3627"];

export default function WeekdayCompare({ storeId, period, store }) {
  const [localPeriod, setLocalPeriod] = useState(period);
  const [wd, setWd] = useState(0);
  const [showIndividual, setShowIndividual] = useState(false);
  const qs = `weekday=${wd}&period_from=${localPeriod?.from || ""}&period_to=${localPeriod?.to || ""}`;
  const { data } = useSWR(`/stores/${storeId}/analytics/weekday-compare?${qs}`, fetcher);

  // Opening hours for selected weekday (used to trim chart X-axis to relevant slots)
  const openWindow = useMemo(() => {
    const cfg = store?.day_configs?.[String(wd)] || {};
    const parseHr = (s) => {
      const n = parseInt(String(s || "").split(":")[0], 10);
      return Number.isFinite(n) ? n : null;
    };
    return { from: parseHr(cfg.open_from), to: parseHr(cfg.open_to) };
  }, [store, wd]);

  // build hourly chart data: x = hour, P10-P90 band + lines per date + avg
  const chartData = useMemo(() => {
    if (!data || data.empty) return [];
    const hours = new Set();
    data.hourly_per_day.forEach((d) => d.hours.forEach((h) => hours.add(h.hour)));
    data.avg_hourly.forEach((h) => hours.add(h.hour));
    let sorted = Array.from(hours).sort((a, b) => a - b);
    // Trim to opening hours when configured (hour h covers [h, h+1))
    if (openWindow.from != null && openWindow.to != null && openWindow.to > openWindow.from) {
      sorted = sorted.filter((h) => h >= openWindow.from && h < openWindow.to);
    } else {
      // fallback: drop sparse edges where count < 50% of max count
      const maxN = Math.max(...data.avg_hourly.map((x) => x.count || 0), 1);
      const keep = new Set(data.avg_hourly.filter((x) => (x.count || 0) >= maxN * 0.5).map((x) => x.hour));
      sorted = sorted.filter((h) => keep.has(h));
    }
    return sorted.map((h) => {
      const row = { hour: `${h}-${h + 1}` };
      const vals = [];
      data.hourly_per_day.forEach((d) => {
        const found = d.hours.find((x) => x.hour === h);
        const v = found ? found.revenue : null;
        row[d.date] = v;
        if (v != null) vals.push(v);
      });
      const avg = data.avg_hourly.find((x) => x.hour === h);
      row.Priemer = avg ? avg.avg_revenue : null;
      if (vals.length > 0) {
        const lo = Math.min(...vals);
        const hi = Math.max(...vals);
        row._min = lo;
        row._range = hi - lo;
      } else {
        row._min = null;
        row._range = null;
      }
      return row;
    });
  }, [data, openWindow]);

  if (!data) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie porovnania" testIdPrefix="weekday-period" />
      <div className="text-[var(--text-secondary)]">Načítavam...</div>
    </div>
  );

  return (
    <div className="space-y-5">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie porovnania" testIdPrefix="weekday-period" />
      <div className="card-flat p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div>
              <h3 className="font-display text-base font-semibold">Porovnanie rovnakých dní v týždni</h3>
              <p className="text-xs text-[var(--text-secondary)] mt-1">Hodinové profily tržieb cez jednotlivé inštancie zvoleného dňa + priemer.</p>
            </div>
            <PinButton
              storeId={storeId}
              kind="chart"
              label={`Graf Porovnanie dní — ${WEEKDAYS_SK[wd]} (${period.from} → ${period.to})`}
              data={{ id: `weekday_chart_${wd}_${period.from}_${period.to}`, weekday: wd, source: "weekday_compare_chart" }}
              period={period}
              testId={`pin-chart-wdc-${wd}`}
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {WEEKDAYS_SK.map((w, i) => (
              <button
                key={i}
                onClick={() => setWd(i)}
                data-testid={`weekday-btn-${i}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  wd === i ? "bg-[var(--brand)] text-white" : "btn-ghost"
                }`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>

        {data.empty ? (
          <div className="text-center text-[var(--text-secondary)] py-8">Pre {WEEKDAYS_SK[wd]} v zvolenom období nie sú dáta.</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--text-secondary)]">
                {data.days.length} {data.days.length === 1 ? "inštancia" : data.days.length < 5 ? "inštancie" : "inštancií"} dňa <span className="font-medium">{WEEKDAYS_SK[wd]}</span> · sivá plocha = rozsah min–max
              </span>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="toggle-individual-days">
                <input
                  type="checkbox"
                  checked={showIndividual}
                  onChange={(e) => setShowIndividual(e.target.checked)}
                  className="accent-[var(--brand)]"
                />
                <span>Zobraziť jednotlivé dni</span>
              </label>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#5E6962" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v} €`} />
                <Tooltip
                  formatter={(v, n) => {
                    if (n === "_min" || n === "_range") return null;
                    return [fmtEur(v), n];
                  }}
                  contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  payload={[
                    { value: "Priemer", type: "line", color: "#1A1F1C" },
                    { value: "Rozsah min–max", type: "rect", color: "#E4E8E5" },
                    ...(showIndividual ? data.hourly_per_day.map((d, idx) => ({
                      value: d.date, type: "line", color: COLORS_LINES[idx % COLORS_LINES.length],
                    })) : []),
                  ]}
                />
                {/* min-max band (invisible base + visible range) */}
                <Area type="linear" dataKey="_min" stackId="band" stroke="none" fill="transparent" isAnimationActive={false} legendType="none" connectNulls={false} />
                <Area type="linear" dataKey="_range" stackId="band" stroke="none" fill="#C9D2CC" fillOpacity={0.55} isAnimationActive={false} legendType="none" connectNulls={false} />
                {showIndividual && data.hourly_per_day.map((d, idx) => (
                  <Line
                    key={d.date}
                    type="linear"
                    dataKey={d.date}
                    stroke={COLORS_LINES[idx % COLORS_LINES.length]}
                    strokeWidth={1.2}
                    dot={false}
                    strokeOpacity={0.7}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                ))}
                <Line type="linear" dataKey="Priemer" stroke="#1A1F1C" strokeWidth={3} dot={{ r: 3, fill: "#1A1F1C" }} isAnimationActive={false} connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <h4 className="font-display text-sm font-semibold mb-2">Sumár dní</h4>
                <div className="card-flat overflow-hidden">
                  <table>
                    <thead>
                      <tr>
                        <th>Dátum</th>
                        <th className="text-right">Tržba</th>
                        <th className="text-right">€/os.hod</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.days].sort((a, b) => (a.date > b.date ? -1 : 1)).map((d) => (
                        <tr key={d.date}>
                          <td className="tabular">{d.date}</td>
                          <td className="text-right tabular">{fmtEur(d.revenue)}</td>
                          <td className="text-right tabular">{fmtEur(d.per_person_hour)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="font-display text-sm font-semibold mb-2">Priemerný hodinový profil</h4>
                <p className="text-xs text-[var(--text-secondary)] mb-2">Stĺpec <span className="font-medium">N</span> = počet dní vstupujúcich do priemeru pre daný slot. Nižšie N → priemer menej spoľahlivý (napr. skrátená otvaracia doba, inventúra).</p>
                <div className="card-flat overflow-hidden">
                  <table>
                    <thead>
                      <tr>
                        <th>Hodina</th>
                        <th className="text-right">Tržba</th>
                        <th className="text-right">Os.</th>
                        <th className="text-right">€/os.hod</th>
                        <th className="text-right" title="Počet dní v priemere">N</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.avg_hourly.map((h) => {
                        const maxN = Math.max(...data.avg_hourly.map((x) => x.count || 0), 1);
                        const low = (h.count || 0) < maxN;
                        return (
                          <tr key={h.hour}>
                            <td className="tabular">{h.hour}-{h.hour + 1}</td>
                            <td className="text-right tabular">{fmtEur(h.avg_revenue)}</td>
                            <td className="text-right tabular">{fmtNum(h.avg_employees, 1)}</td>
                            <td className="text-right tabular">{fmtEur(h.avg_per_person_hour)}</td>
                            <td
                              className={`text-right tabular ${low ? "text-[var(--warning)] font-medium" : "text-[var(--text-muted)]"}`}
                              title={low ? `Pozor: tento slot má len ${h.count} z ${maxN} dní — priemer je menej spoľahlivý` : `${h.count} dní`}
                            >
                              {h.count}
                            </td>
                            <td className="text-right">
                              <PinButton
                                storeId={storeId}
                                kind="weekday"
                                label={`${WEEKDAYS_SK[wd]} ${h.hour}-${h.hour + 1}: Tržba ${fmtEur(h.avg_revenue)}, €/os.hod ${fmtEur(h.avg_per_person_hour)}, N=${h.count}`}
                                data={{ weekday: wd, hour: h.hour, source: "weekday_compare", ...h }}
                                period={period}
                                testId={`pin-wdc-${wd}-${h.hour}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
